// src/components/LayerPanel/FeatureColorSwatch.jsx
//
// The per-type color swatch used on FEATURES-tab rows and Active Layers tray
// rows. The swatch shows the EFFECTIVE color (so it doubles as the legend) and
// opens a native color picker; a small reset returns to the palette default.
// Available whether or not the type is toggled on — a pre-set takes effect when
// the layer turns on. `well_pad` is rendered by PadLayer rather than the generic
// renderer, and PadLayer reads the same override store, so its swatch is
// editable too -- it was a read-only legend chip while that wiring was missing.

import { RotateCcw } from "lucide-react";
import { useFeatureColorStore } from "../../stores/useFeatureColorStore";
import { effectiveFeatureColor } from "../../constants/featureColors";

export default function FeatureColorSwatch({ assetType, size = 13 }) {
  const overrides = useFeatureColorStore((s) => s.overrides);
  const setColor = useFeatureColorStore((s) => s.setColor);
  const resetColor = useFeatureColorStore((s) => s.resetColor);

  const color = effectiveFeatureColor(assetType, overrides);
  // well_pad USED TO BE READ-ONLY HERE. PadLayer renders pads instead of the
  // generic feature renderer, and it hardcoded its colour, so the picker wrote
  // an override that nothing read -- the control opened and did nothing.
  // PadLayer now reads this same store, so the swatch is editable like the rest.
  const editable = true;
  const overridden = !!overrides[assetType];

  if (!editable) {
    return (
      <span
        title="Legend color"
        style={{ background: color, width: size, height: size }}
        className="block rounded-sm border border-slate-600 flex-shrink-0"
      />
    );
  }

  return (
    <span className="flex items-center gap-0.5 flex-shrink-0">
      <label className="relative cursor-pointer leading-none" title="Set color">
        <span
          style={{ background: color, width: size, height: size }}
          className="block rounded-sm border border-slate-600"
        />
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(assetType, e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label={`Color for ${assetType}`}
        />
      </label>
      {overridden && (
        <button
          onClick={() => resetColor(assetType)}
          title="Reset to default color"
          className="text-fg-secondary hover:text-gray-200 leading-none"
        >
          <RotateCcw size={9} />
        </button>
      )}
    </span>
  );
}
